import type { ObjectId } from "@bbj-types/utils";
import { type ReactNode, createContext, useContext, useMemo } from "react";
import type { IAutocompleteAppellationsProps } from "./index";
import { useAutocompleteAppellationsGroups } from "./logics/groups";
import type { useAutocompleteAppellationsOptions } from "./logics/options";
import type { useAutocompleteAppellationsSelected } from "./logics/selected";

export interface IAutocompleteAppellationsContext
  extends ReturnType<typeof useAutocompleteAppellationsGroups>,
    Omit<ReturnType<typeof useAutocompleteAppellationsSelected>, "handleAutocompleteClose"> {}

const context = createContext<IAutocompleteAppellationsContext | undefined>(undefined);

export const useAutocompleteAppellationsContext = () => {
  const value = useContext(context);
  if (value === undefined) {
    throw new Error("AutocompleteAppellationsProvider not found using useAutocompleteAppellationsContext");
  }
  return value;
};

export interface IAutocompleteAppellationsProviderProps
  extends Omit<ReturnType<typeof useAutocompleteAppellationsSelected>, "handleAutocompleteClose"> {
  value: IAutocompleteAppellationsProps["value"];
  valueSet: Set<ObjectId>;
  onChange: IAutocompleteAppellationsProps["onChange"];
  options: ReturnType<typeof useAutocompleteAppellationsOptions>["options"];
  children: ReactNode;
}

export const AutocompleteAppellationsProvider = ({
  value,
  valueSet,
  onChange,
  options,
  selectedOptionsGroups,
  handleClickSelectedOptionGroup,
  handleClickSelectedOption,
  children,
}: IAutocompleteAppellationsProviderProps) => {
  const { groupsLength, getOptionGroupLabel, getOptionGroupStatus, handleClickOptionGroup } =
    useAutocompleteAppellationsGroups(value, valueSet, onChange, options);

  const contextValue: IAutocompleteAppellationsContext = useMemo(
    () => ({
      groupsLength,
      getOptionGroupLabel,
      getOptionGroupStatus,
      handleClickOptionGroup,
      selectedOptionsGroups,
      handleClickSelectedOptionGroup,
      handleClickSelectedOption,
    }),
    [
      groupsLength,
      getOptionGroupLabel,
      getOptionGroupStatus,
      handleClickOptionGroup,
      selectedOptionsGroups,
      handleClickSelectedOptionGroup,
      handleClickSelectedOption,
    ]
  );

  return <context.Provider value={contextValue}>{children}</context.Provider>;
};
